import { CompartmentCard } from "@/src/components/cards";
import { openDialog, openDrawer } from "@/src/components/dialogs";
import { InfiniteFetchableList } from "@/src/components/fetchableLists";
import { StatisticsReport } from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import { uid, getName, getAge } from "@/src/utils/utils";
import { useState } from "react";
import { PatientDashboard } from "../patients/patientDashboard";
import { HtmlDiv } from "../documents/htmlDiv";

export default function ReportPatientsList({
  report,
}: {
  report: StatisticsReport;
}) {
  let [searchKey, setSearchKey] = useState<string>("");
  function escapeCsvValue(value: any) {
    // Ensure it's a string
    let str = String(value);
  
    // Escape existing double quotes by doubling them
    str = str.replace(/"/g, '""');
  
    // If the field contains commas, quotes, newlines or carriage returns,
    // enclose the entire field in quotes
    if (/[",\n\r]/.test(str)) {
      str = `"${str}"`;
    }
  
    return str;
  }
  
  function generateCSV(data: any) {
    if (!Array.isArray(data) || data.length === 0) {
      return "";
    }
  
    const sectionNames = Object.keys(data[0]);
  
    // For each section, figure out how many columns (keys) it has
    const sectionColumnCounts = sectionNames.map((sectionName) => {
      const columns = Object.keys(data[0][sectionName] || {});
      return columns.length;
    });
  
    // --- First CSV row: “simulating” colspan
    let row1: string[] = [];
    sectionNames.forEach((sectionName, idx) => {
      const columnsInSection = sectionColumnCounts[idx];
      // Put the section name in the first cell
      row1.push(escapeCsvValue(sectionName));
      // Fill the rest of that section’s columns with empty cells
      for (let i = 1; i < columnsInSection; i++) {
        row1.push(escapeCsvValue(""));
      }
    });
  
    let row2: string[] = [];
    sectionNames.forEach((sectionName) => {
      const columnNames = Object.keys(data[0][sectionName] || {});
      columnNames.forEach((colName) => {
        row2.push(escapeCsvValue(colName));
      });
    });
  
    // --- Data rows: combine all sections’ columns for each row
    const dataRows = data.map((item) => {
      let row: string[] = [];
      sectionNames.forEach((sectionName) => {
        const columnNames = Object.keys(item[sectionName] || {});
        columnNames.forEach((colName) => {
          row.push(escapeCsvValue(item[sectionName][colName]));
        });
      });
      return row;
    });
  
    // --- Combine all rows into CSV format
    const csvLines = [
      row1.join(","),      // 1st row
      row2.join(","),      // 2nd row
      ...dataRows.map((row) => row.join(",")), // data rows
    ];
  
    // Final CSV string
    return csvLines.join("\n");
  }

  function downloadCSV(data: any) {
    let csv = generateCSV(data);
    var csvFile = new Blob([csv], { type: "text/csv" });
    var downloadLink = document.createElement("a");
    downloadLink.download = "data.csv";
    downloadLink.href = window.URL.createObjectURL(csvFile);
    downloadLink.style.display = "none";
    document.body.appendChild(downloadLink);
    downloadLink.click();
  }

  return (
    <CompartmentCard
      title="Liste des patients"
      buttonTitle="Exporter"
      onClick={async () => {
        let data = await apiFetch("statistics", "loadRawData", {
          reportId: report.id,
        });

        setTimeout(() => {
          downloadCSV(data);
        }, 1000);

        console.log(data);
        let html = "<table class='white-bg bs br'> <tr>";
        let sectionNames = Object.keys(data[0] || {});
        for (let sectionName of sectionNames) {
          let columnNames = Object.keys(data[0][sectionName] || {});
          html += `<th colspan="${columnNames.length}">${sectionName}</th>`;
        }
        html += `</tr>`;

        html += `<tr>`;
        for (let sectionName of sectionNames) {
          for (let columnName of Object.keys(data[0][sectionName] || {})) {
            html += `<th>${columnName}</th>`;
          }
        }
        html += `</tr>`;

        for (let item of data) {
          html += `<tr>`;
          for (let sectionName of sectionNames) {
            for (let columnName of Object.keys(item[sectionName] || {})) {
              html += `<td>${item[sectionName][columnName]}</td>`;
            }
          }
          html += `</tr>`;
        }

        html += `</table>`;
       

        await openDialog({
          id: "reportPatientsListDialog",
          title: "Liste des patients",
          content: (
            <div>
              <HtmlDiv>{html}</HtmlDiv>
            </div>
          ),
        });
      }}
    >
      <div className="c1 gap10">
        <InfiniteFetchableList
          queryKey={["ReportsPagePatientsList", searchKey]}
          getFunction={(limit, offset, searchKey) => {
            return apiFetch("statistics", "loadStatisticsReportPatients", {
              reportId: report.id,
              searchKey: searchKey,
              offset,
              limit,
            });
          }}
          searchKey={searchKey}
          onSearch={(newValue) => {
            setSearchKey(newValue || "");
          }}
        >
          {(patients) => {
            return (
              <div key={uid()} className="c1 gap10">
                {patients?.map((patient) => {
                  let activeGroups: Record<string, boolean> = {};
                  Object.entries(report.patientGroups || {})?.forEach(
                    ([groupName, patientsIds]) => {
                      if (patientsIds.includes(patient.id || ""))
                        activeGroups[groupName] = true;
                    }
                  );
                  return (
                    <div
                      key={uid()}
                      className="card"
                      onClick={async () => {
                        await openDrawer({
                          id: "patientDashboardDrawer",
                          additionalClasses: ["big"],
                          title: "Dossier patient",
                          content: (
                            <PatientDashboard
                              currentPatient={patient}
                            ></PatientDashboard>
                          ),
                        });
                      }}
                    >
                      <div
                        className={`bold ${
                          patient.sex == "femme" ? "pink-color" : "blue-color"
                        }`}
                      >
                        {getName(patient)}
                      </div>
                      <div className="gray">{getAge(patient)}</div>
                      <div className="">
                        {Object.keys(activeGroups).map((groupName) => {
                          return (
                            <span key={uid()} className="gray">
                              {groupName}
                            </span>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          }}
        </InfiniteFetchableList>
      </div>
    </CompartmentCard>
  );
}
